import {
  memo,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import {
  Outlet,
  useLocation,
  useMatch,
  useSearchParams,
} from "react-router-dom";

import { useConfirmingLeave } from "@lhd/app/index";
import {
  ROUTES,
  Status,
  UNSAVED_CHANGES_WARNING_MESSAGE,
} from "@lhd/constants";
import { user } from "@lhd/features/users/selector";
import {
  getCompanyId,
  getEffectiveIsDisplayInNewTab,
  IS_DISPLAY_IN_NEW_TAB_PARAM,
} from "@lhd/utility";
import { useSelector } from "react-redux";
import Header from "../Header";
import Loading from "../Loading";
import ModalConfirm from "../Modal/ModalConfirm";
import { getIsCollapse as getIsCollapseSearchArea } from "../SearchAreaBox/searchAreaSelector";
import { getIsOpen as getIsOpenProjectStatusFilter } from "@lhd/features/ProjectManager/components/ProjectStatusFilter/projectStatusFilterSelector";
import SideBar from "../SideBar";
import SideBarCompanyList from "../SideBarCompanyList";
import { getIsCollapse, getIsMobileMenuOpen } from "./sidebarSelector";
import { Styles } from "./styled";
import { useDispatch } from "react-redux";
import {
  setIsCollapse as setIsCollapseSidebarAction,
  setIsMobileMenuOpen,
} from "./sidebarSlice";
import { getDisplaySize } from "@lhd/components/DisplaySizeSelector/selector";
import { isMobile, isTablet, isMobileOnly } from "react-device-detect";
import WorkerNotiInvite from "./WorkerNotiInvite";
import { useTokenRotationCableReset } from "@lhd/hooks";
import { isSalaryWorkerFilterRoute } from "@lhd/features/Salary/utils/salaryWorkerFilterRoutes";

interface CommonLayoutProps {
  isHideSideBar?: boolean;
}

const CommonLayout: React.FC<CommonLayoutProps> = ({ isHideSideBar }) => {
  // const [isCollapse, setIsCollapse] = useState(true);
  const isCollapse = useSelector(getIsCollapse);
  const text_scale = useSelector(getDisplaySize);
  const [searchParams] = useSearchParams();
  const dispatch = useDispatch();

  const [isOpenConfirm, setOpenConfirm] = useState<boolean>(false);

  useTokenRotationCableReset();

  const auth = useSelector(user);
  const companyId = getCompanyId();

  const matchWorkerList = useMatch(`/${ROUTES.WORKERS}`);
  const matchWorkerListV2 = useMatch(`/${ROUTES.WORKERS}/v2`);
  const matchCustomerList = useMatch(`/${ROUTES.CUSTOMER_LIST}`);
  const matchProjectCustomerList = useMatch(
    `/${ROUTES.PROJECT_MANAGE}/${ROUTES.CUSTOMER_LIST}`,
  );
  const matchProjectManagerList = useMatch(`/${ROUTES.PROJECT_MANAGER}`);
  const matchWorkerCreate = useMatch(`/${ROUTES.WORKERS}/create`);
  const matchListMess = useMatch(`/${ROUTES.LIST_MESS}`);
  const matchAgentSummaries = useMatch(`/${ROUTES.AGENT_SUMMARIES}`);
  const matchBusinessRpgList = useMatch(`/${ROUTES.BUSINESS_RPG}`);
  const matchBusinessRpgPlay = useMatch(`/${ROUTES.BUSINESS_RPG_PLAY}`);
  const matchBusinessTripReportsList = useMatch(
    `/${ROUTES.BUSINESS_TRIP_REPORTS}`,
  );
  const matchBusinessFlowList = useMatch(`/${ROUTES.BUSINESS_FLOW}`);
  const matchBusinessManualsList = useMatch(`/${ROUTES.BUSINESS_MANUALS}`);
  const matchRolePermissionsList = useMatch(`/${ROUTES.ROLE_PERMISSIONS}`);
  const matchLatestTrialBalanceFire = useMatch(
    `/${ROUTES.LATEST_TRIAL_BALANCE_FIRE}`,
  );
  const matchLatestTrialBalanceFireDetail = useMatch(
    `/${ROUTES.LATEST_TRIAL_BALANCE_FIRE}${ROUTES.DETAIL}`,
  );
  const matchCompanyPoliciesList = useMatch(`/${ROUTES.COMPANY_POLICIES}`);

  const location = useLocation();

  useEffect(() => {
    if (isMobileOnly && (matchListMess || matchAgentSummaries)) {
      dispatch(setIsMobileMenuOpen(false));
    }
  }, [location.pathname, isMobileOnly, dispatch]);

  const isBusinessRpgRoute = !!matchBusinessRpgList || !!matchBusinessRpgPlay;
  const isMobileOrTablet = isMobile || isTablet;
  const isMobileTrackingPath =
    location.pathname === ROUTES.TRACKING_TIME ||
    location.pathname.startsWith(`${ROUTES.TRACKING_TIME}/`);
  const isMobileELearningPath =
    location.pathname === `/${ROUTES.Electronic_Learning}` ||
    location.pathname.startsWith(`/${ROUTES.Electronic_Learning}/`);
  const hideLayoutOnMobileTracking = isMobileOrTablet && isMobileTrackingPath;
  const hideLayoutOnMobileELearning = isMobileOrTablet && isMobileELearningPath;
  const hideHeaderLayout =
    hideLayoutOnMobileTracking || hideLayoutOnMobileELearning;
  const isMobileMenuOpen = useSelector(getIsMobileMenuOpen);
  const isLatestTrialBalanceFireRoute =
    !!matchLatestTrialBalanceFire || !!matchLatestTrialBalanceFireDetail;

  const effectiveHideSideBar =
    !!isHideSideBar ||
    hideLayoutOnMobileTracking ||
    hideLayoutOnMobileELearning ||
    isBusinessRpgRoute ||
    (isMobile && !isMobileMenuOpen) ||
    isLatestTrialBalanceFireRoute;
  const headerCollapse = effectiveHideSideBar ? true : isCollapse;

  const isPadding =
    !location.pathname.includes("/electronic_learning/course_detail") &&
    !hideLayoutOnMobileTracking &&
    !hideLayoutOnMobileELearning &&
    !!matchBusinessRpgList &&
    !isLatestTrialBalanceFireRoute;
  const isProjectListGanttChart =
    !!matchProjectManagerList && searchParams.get("pmview") === "chart";
  const isProjectManagerRoute = location.pathname.includes(
    `/${ROUTES.PROJECT_MANAGER}`,
  );
  const isHaveSearchAreaBox =
    isSalaryWorkerFilterRoute(location.pathname) ||
    !!matchCustomerList ||
    !!matchProjectCustomerList ||
    matchWorkerList ||
    matchWorkerListV2 ||
    matchWorkerCreate ||
    matchListMess ||
    matchBusinessRpgList ||
    matchBusinessFlowList ||
    matchBusinessManualsList ||
    matchBusinessTripReportsList ||
    matchCompanyPoliciesList ||
    matchRolePermissionsList;
  const isHaveProjectStatusFilter =
    !!matchProjectManagerList && searchParams.get("pmview") !== "chart";
  const isCollapseSearchAreaBox = useSelector(getIsCollapseSearchArea);
  const isOpenProjectStatusFilter = useSelector(getIsOpenProjectStatusFilter);

  const nameCompany = useMemo(() => {
    return (
      auth?.companies.find((company) => company.id.toString() == companyId)
        ?.name || auth?.companies.find((company) => company.is_work_default)
    );
  }, [auth, companyId]);
  const divRef = useRef<HTMLDivElement>(null);
  const [divHeight, setDivHeight] = useState(0);
  const isDisplayInNewTab = getEffectiveIsDisplayInNewTab(
    searchParams.get(IS_DISPLAY_IN_NEW_TAB_PARAM) === "true",
  );
  const searchAreaOffset = isHaveSearchAreaBox
    ? isCollapseSearchAreaBox
      ? 286
      : 40
    : 0;
  const businessRpgSearchAreaOffset = matchBusinessRpgList
    ? searchAreaOffset
    : 0;
  const projectStatusFilterOffset = isHaveProjectStatusFilter
    ? isOpenProjectStatusFilter
      ? 260
      : 40
    : 0;

  useEffect(() => {
    const updateHeight = () => {
      if (divRef.current) {
        setDivHeight(divRef.current.offsetHeight);
      }
    };
    requestAnimationFrame(updateHeight);
    return () => {};
  }, [nameCompany]);

  const [headerHeight, setHeaderHeight] = useState(48);

  useEffect(() => {
    if (hideHeaderLayout) {
      setHeaderHeight(0);
      return;
    }
    const headerEl = document.getElementById("app-header");
    if (!headerEl) return;

    const resizeObserver = new ResizeObserver(() => {
      setHeaderHeight(headerEl.offsetHeight);
    });

    resizeObserver.observe(headerEl);
    return () => {
      resizeObserver.disconnect();
    };
  }, [hideHeaderLayout]);

  const {
    confirmingState,
    setConfirmingState,
    callbackEvent,
    setCallbackEvent,
    isExecutingConfirmedActionRef,
  } = useConfirmingLeave();

  const beforeUnload = useCallback(
    (e: BeforeUnloadEvent) => {
      if (location.pathname === "/" + ROUTES.COMPANIES) return;
      e.preventDefault();
      e.returnValue = "";
    },
    [location.pathname],
  );

  const removeListener = useCallback(() => {
    window.removeEventListener("beforeunload", beforeUnload);
  }, [beforeUnload]);

  const setIsCollapse = (val: boolean) => {
    dispatch(
      setIsCollapseSidebarAction({
        isCollapse: val,
      }),
    );
  };

  const onConfirmRouteChange = useCallback(() => {
    setConfirmingState(null);
    if (callbackEvent && confirmingState?.status === Status.OpenModal) {
      isExecutingConfirmedActionRef.current = true;
      let actionResult: void | Promise<void>;
      try {
        actionResult = callbackEvent();
      } catch (error) {
        isExecutingConfirmedActionRef.current = false;
        throw error;
      }
      void Promise.resolve(actionResult).finally(() => {
        isExecutingConfirmedActionRef.current = false;
      });
      setCallbackEvent(null);
    }
    setOpenConfirm(false);
    removeListener();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callbackEvent,
    confirmingState?.status,
    isExecutingConfirmedActionRef,
    removeListener,
    setCallbackEvent,
    setConfirmingState,
  ]);

  const onRejectRouteChange = useCallback(() => {
    setCallbackEvent(null);
    setConfirmingState(
      confirmingState?.clearOnCancel ? null : { status: Status.UnSave },
    );
    setOpenConfirm(false);
  }, [confirmingState?.clearOnCancel, setCallbackEvent, setConfirmingState]);

  useEffect(() => {
    if (confirmingState) {
      window.addEventListener("beforeunload", beforeUnload);
      if (confirmingState.status === Status.OpenModal) {
        setOpenConfirm(true);
      }
    }
    return removeListener;
  }, [beforeUnload, confirmingState, removeListener]);

  return (
    <div>
      {!hideHeaderLayout && <Header isCollapse={headerCollapse} />}
      <WorkerNotiInvite />
      {!effectiveHideSideBar && (
        <>
          <SideBarCompanyList
            key={auth?.companies?.length}
            isCollapse={isCollapse}
            divHeight={divHeight}
          />
          <SideBar
            isCollapse={isCollapse}
            setIsCollapse={setIsCollapse as any}
            divHeight={divHeight}
          />
        </>
      )}

      <Styles>
        <div
          style={
            {
              paddingTop: hideHeaderLayout ? 0 : `${headerHeight}px`,
              "--header-height": `${headerHeight}px`,
              paddingLeft: isDisplayInNewTab
                ? (isCollapse ? 76 : 0) +
                  (isHaveSearchAreaBox
                    ? isCollapseSearchAreaBox
                      ? 252
                      : 16
                    : 0) +
                  projectStatusFilterOffset
                : effectiveHideSideBar
                ? businessRpgSearchAreaOffset
                : (isCollapse ? 76 : 308) +
                  searchAreaOffset +
                  projectStatusFilterOffset,
              minHeight: "100vh",
              position: "relative",
              transition: "0.1s",
            } as React.CSSProperties
          }
          className="container_main"
          id="container_main"
        >
          <div
            style={{
              padding: isPadding
                ? isProjectListGanttChart
                  ? "0px 12px 0px 12px"
                  : isProjectManagerRoute
                  ? "12px 12px 0px 12px"
                  : matchBusinessRpgList
                  ? "12px 12px 0px 12px"
                  : "12px 12px 0px 24px"
                : "12px 12px 12px 24px",
              paddingBottom: 0,
            }}
            className="main"
            id="main-layout"
          >
            <Suspense fallback={<Loading />}>
              <Outlet />
            </Suspense>
          </div>
        </div>
      </Styles>
      <ModalConfirm
        isOpen={isOpenConfirm}
        onCancel={() => onRejectRouteChange()}
        onOk={() => onConfirmRouteChange()}
        content={UNSAVED_CHANGES_WARNING_MESSAGE}
        text_scale={text_scale}
      />
    </div>
  );
};

CommonLayout.propTypes = {
  isHideSideBar: PropTypes.bool,
};
export default memo(CommonLayout);
